import * as React from 'react';
import autobind from 'autobind-decorator';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import { Row, Col } from 'antd';
import { resolve } from 'inversify-react';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { FormatService } from 'src/core/services/format.service';
import { ContextService } from 'src/core/services/context.service';
import { TabLanguageController } from 'src/core/ui/language/tab-language.controller';
import { DateView } from 'src/core/ui/date.view';
import { TextView } from 'src/core/ui/text.view';
import { AllotmentRangeTypeEditorController } from 'src/app/products/allotment-range/parts/allotment-range-type-editor.controller';
import { AllotmentRangeDailyListDataService } from 'src/app/products/allotment-range-daily/list/allotment-range-daily-list.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	rangeDailies: AllotmentRangeDailyItem[];
}

interface PropsFromDispatch {
	rangeDailiesLoaded: (allotment: AllotmentRangeItem, rangeDailies: AllotmentRangeDailyItem[]) => any;
}

interface Props extends ComponentViewProps<AllotmentRangeItem> {
	mode: ControlMode;
	allotment: AllotmentItem;
	contextService: ContextService;
}

type ConnectedProps = Props & PropsFromDispatch & PropsFromState;

@observer
class AllotmentRangeGeneralView extends React.Component<ConnectedProps, any> {
	@observable
	protected enable: boolean;

	private allotmentRangeDailyListdataService: AllotmentRangeDailyListDataService;

	@resolve
	private formatService: FormatService;

	getDates(allotmentRange: AllotmentRangeItem): Date[] {
		let dates: Date[] = [];
		let today = allotmentRange.dateFrom;
		today = moment.utc(today).startOf('day').toDate();
		while (today <= allotmentRange.dateTo) {
			dates.push(today);
			today = moment.utc(today).add(1, 'days').toDate();
		}
		return dates;
	}

	@autobind
	private createNewAllotmentRangeDaily(day: number, date: Date) {
		return this.allotmentRangeDailyListdataService.add(this.props.model.dailyLimit, this.props.model.id, day, date);
	}

	private async deleteDailies(reload: boolean) {
		const items = this.allotmentRangeDailyListdataService.items.map(o => o);
		let number = await this.allotmentRangeDailyListdataService.delete(items);
		reload && this.props.rangeDailiesLoaded(this.props.model, []) ;
	}

	async refresh() {
		let res = await this.deleteDailies(false);
		let dates = this.getDates(this.props.model);
		const promises: Promise<any>[] = [];
		for (let i = 0; i < dates.length; i++) {
			promises.push(this.createNewAllotmentRangeDaily(i, dates[i]));
		}
		await Promise.all(promises);
		this.props.rangeDailiesLoaded(this.props.model, this.allotmentRangeDailyListdataService.items.slice());
	}

	isValidRangeDate(dateFrom: Date, dateTo: Date): boolean {
		let isValid = dateFrom <= dateTo;
		return isValid;
	}

	public componentDidMount() {
		this.enable = (this.props.model) ? ((this.props.model.allotmentRangeType == 1 || this.props.model.allotmentRangeType == 2)) : false;
		this.enable = this.enable && (this.props.mode == "edit");
		this.allotmentRangeDailyListdataService = AllotmentRangeDailyListDataService.make(this.props.contextService, this.formatService);
		this.allotmentRangeDailyListdataService.items = this.props.rangeDailies || [];
	}

	public componentWillReceiveProps(nextProps: Readonly<ConnectedProps>, nextContext: any) {
		if (this.props.mode != nextProps.mode) {
			this.enable = (this.props.model) ? ((this.props.model.allotmentRangeType == 1 || this.props.model.allotmentRangeType == 2)) : false;
			this.enable = this.enable && (nextProps.mode == "edit");
		}
		if (nextProps.rangeDailies != this.props.rangeDailies) {
			this.allotmentRangeDailyListdataService.items = nextProps.rangeDailies || [];
		}
	}

	public render() {
		const model = this.props.model;
		const displayNames = (model as any).dynamicProperties['translations'];
		const array: TabLanguageModel[] = [{ title: 'Description', dynamicProperties: displayNames, required: false, rows: 2 }];

		return (
			<div>
				<Row gutter={8}>
					<Col xs={24} lg={12}>
						<TabLanguageController
							dynamicProperties={array}
							mode={this.props.mode}
							setBusy={this.props.setBusy}
							isRichTextBox={false}
							model={this.props.model}
						/>
					</Col>
					<Col xs={24} lg={12}>
						<AllotmentRangeTypeEditorController
							mode={this.props.mode}
							onChanged={(value) => {
								model.allotmentRangeType = value;
								this.enable = (value == 1 || value == 2) && (this.props.mode == "edit");
								if (!this.enable) {
									this.deleteDailies(true);
									this.props.model.dailyLimit = null;
									this.props.model.totalLimit = null;
								}
								if (model.dailyLimit != null && this.enable) {
									this.refresh();
								}

							}}
							value={model.allotmentRangeType}
							setBusy={this.props.setBusy}
						/>
					</Col>
				</Row>
				<Row gutter={8}>
					<Col xs={24} lg={12}>
						<DateView
							mode={this.props.mode}
							format='date'
							label='Date From'
							value={model.dateFrom}
							onChanged={newValue => {
								model.dateFrom = newValue;
								if (this.props.model.dailyLimit != null && this.enable)
									this.refresh();
							}}
							dateUsage="utc"
						/>

					</Col>
					<Col xs={24} lg={12}>
						<DateView
							mode={this.props.mode}
							format='date'
							label='Date To'
							value={model.dateTo}
							onChanged={date => {
								model.dateTo = date;
								if (this.props.model.dailyLimit != null && this.enable)
									this.refresh();

							}}
							dateUsage="utc"
						/>

					</Col>
				</Row>
				<Row gutter={8}>
					<Col xs={24} lg={12}>
						<TextView
							mode={this.props.mode == "edit" && this.enable ? "edit" : "display"}
							required={model.allotmentRangeType == 1}
							label='Total limit'
							value={model.totalLimit}
							onChanged={newVal => {
								let val = 0;
								if (newVal !== "" && newVal != null) {
									val = parseFloat(newVal);
									if (isNaN(val)) {
										val = 0;
									}
								} else {
									val = 0;
								}
								model.totalLimit = val;
								this.deleteDailies(true);
								model.dailyLimit = null;
							}} />
					</Col>
					<Col xs={24} lg={12}>
						<TextView
							mode={this.props.mode == "edit" && this.enable ? "edit" : "display"}
							required={model.allotmentRangeType == 1}
							label='Daily limit'
							value={model.dailyLimit}
							onChanged={newVal => {
								let val = 0;
								if (newVal !== "" && newVal != null) {
									val = parseFloat(newVal);
									if (isNaN(val)) {
										val = 0;
									}
								} else {
									val = 0;
								}
								model.dailyLimit = val;
								if (model.totalLimit != null || this.allotmentRangeDailyListdataService.items.length === 0 && val > 0) {
									this.refresh();
								} else {
									const items = this.allotmentRangeDailyListdataService.items.slice();
									for (const item of items) {
										item.dailyLimit = val;
									}
								}
								model.totalLimit = null;
							}}
						/>
					</Col>
				</Row>
			</div>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: Props,
): PropsFromState => ({
	rangeDailies: (state.otherLoadedEntities.find(e => e.entity == props.model.entity) as AllotmentRangeItem).allotmentRangeDailies,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	rangeDailiesLoaded: (
		allotment: AllotmentRangeItem,
		rangeDailies: AllotmentRangeDailyItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(allotment, rangeDailies, "allotmentRangeDailies")),
	
});

const connected = connect(mapStateToProps, mapDispatchToProps)(AllotmentRangeGeneralView);
export { connected as AllotmentRangeGeneralView };



// WEBPACK FOOTER //
// ./src/app/products/allotment-range/form/parts/allotment-range-general.view.tsx